/* eslint-disable max-lines */
/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import React, { useState, useCallback, useContext, PropsWithChildren, useEffect, useRef } from "react";
import { Form, Formik, FormikConfig, FormikHelpers } from "formik";
import classNames from "classnames";
import { Typography, Grid } from "@mui/material";
import { useMutation } from "@apollo/client";
import * as yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import { Card } from "components/haven-design";
import { Button, LinkButton } from "components/haven-design/buttons";
import { Login as LoginContext } from "components/viewer/context";
import { getVisitorId, initializeUserLogin } from "services/analytics";
import {
  UserLoginPreflight,
  UserLoginPreflightVariables,
  LoginMethod,
  UserLogin,
  UserLoginVariables,
  UserPhoneLogin,
  UserPhoneLoginVariables,
  UserGetCode,
  UserGetCodeVariables,
} from "types/gql-types";
import { getReturnToUrl } from "utils/login";
import { formatPhone } from "utils/phone";
import { loginWithHavenToken } from "utils/general-utils";
import { LoginFormValues, Section, Handle, Page, PageType } from "../type";
import { isEmailIdentifier, sanitizePhoneNumber } from "../utils";
import { SIGN_IN_ATTEMPT, USER_GET_CODE, USER_LOGIN, USER_PHONE_LOGIN } from "../query";
import LoginModalContext from "../context/modal";
import styles from "./Login.module.css";
import { loginStyles, loginAuthColumnSx } from "./styles";
import { LoginCardTitle, LoginCardSubtitle, LoneGrid } from "./shared-components";
import SignUpCard from "./sign-up-card";
import WaitToVerify from "./wait-to-verify";
import LoginFormMobile from "./login-form-mobile";
import LoginForm from "./login-form";
import { useRememberedEmail } from "./hooks";

const components: { [K in Handle<PageType>["page"]]: Page<K> } = {
  create: SignUpCard,
  "wait-to-verify": WaitToVerify,
  login: () => null,
};

type LoginContainerProps = {
  phoneNumberAwaitingCode: string | false;
  headerText?: string | React.ReactNode;
  subheaderText?: string;
}

const LoginFormContainer: React.FC<PropsWithChildren<LoginContainerProps>> = (props) => {
  const { inModal } = useContext(LoginModalContext);

  return (
    <Form className={styles.form}>
      <Card className={classNames(styles.loginCard, { [styles.borderlessCard]: inModal })}>
        <div style={{ margin: "8px" }}>
          <Grid
            container
            spacing={2}
            direction="column"
            alignItems="center"
            justifyContent="center"
            sx={{ width: "100%" }}
          >
            <Grid size={{ xs: 12 }} sx={{ width: "100%" }}>
              <LoginCardTitle title={props.headerText || "Welcome Back!" } />
              {(!props.headerText && !props.phoneNumberAwaitingCode) && <LoginCardSubtitle>
              Property managers and applicants can login here.
              </LoginCardSubtitle>}
              {props.phoneNumberAwaitingCode && (
                <LoginCardSubtitle>
                Enter the 4-digit code sent to you at {formatPhone(props.phoneNumberAwaitingCode)}
                </LoginCardSubtitle>
              )}
              {props.subheaderText && (
                <Typography variant="body1" style={{
                  textAlign: "center",
                  fontSize: ".9rem",
                  paddingBottom: "0.8em",
                  paddingTop: 0,
                  fontWeight: 300,
                }}>{props.subheaderText}</Typography>)}
            </Grid>
            <Grid size={{ xs: 12 }} sx={{ width: "100%" }}>
              {props.children}
            </Grid>
          </Grid>
        </div>
      </Card>
    </Form>
  );
};

const SignInLink: React.FC<{ onClick?: () => void; link?: boolean }> =
({ onClick, link = false }) => {
  const message = "Already have an account?";
  const buttonText = "Login to your Account";
  const location = useLocation();
  const returnToUrl = getReturnToUrl();
  const retunToCurrent = returnToUrl ? `?return-to=${returnToUrl}` : "";
  const loginLink = `/login${location.search || retunToCurrent}`;
  return (
    <LoneGrid wide>
      <Typography variant="body2" style={loginStyles.signUpLabel}>
        {message}
      </Typography>

      <Grid size={{ xs: 12 }} sx={loginAuthColumnSx}>
        {link ? (
          <LinkButton
            color="secondary"
            size="L"
            fullWidth
            className={styles.loginButton}
            to={loginLink}
          >
            {buttonText}
          </LinkButton>
        ) : (
          <Button
            id="sign-up"
            type="button"
            color="secondary"
            size="L"
            fullWidth
            className={styles.loginButton}
            onClick={onClick}
          >
            {buttonText}
          </Button>
        )}
      </Grid>
    </LoneGrid>
  );
};

const processValues = (values: LoginFormValues) => ({
  ...values,
  identifier: values.identifier.trim(),
});

const onDisplayError = (handlers: FormikHelpers<LoginFormValues>, error: unknown) => {
  let errorMessage = "";

  if (error instanceof Error) errorMessage = (error as Error).message;
  else errorMessage = error as string;

  const errorElementIds = errorMessage.toLowerCase().includes("password") ? ["password"] : ["identifier", "code"];
  errorElementIds.forEach((element) => handlers.setFieldError(element, errorMessage));
};

const schema = yup.object({
  identifier: yup.string().required("A valid email or US mobile phone number is required."),

  password: yup
    .mixed()
    .when("identifier", {
      is: (identifier: unknown) => isEmailIdentifier(String(identifier ?? "")),
      then: () => yup.string().required(),
      otherwise: (mixedSchema) => mixedSchema,
    }),

  remember: yup
    .mixed()
    .when("identifier", {
      is: (identifier: unknown) => isEmailIdentifier(String(identifier ?? "")),
      then: () => yup.boolean(),
      otherwise: (mixedSchema) => mixedSchema,
    }),

  code: yup
    .mixed()
    .when(["identifier", "isAwaitingCode"], {
      is: (identifier: unknown, isAwaitingCode: unknown) =>
        !isEmailIdentifier(String(identifier ?? "")) && Boolean(isAwaitingCode),
      then: () =>
        yup
          .string()
          .required("Please enter the 4-digit verification code")
          .min(4, "Please enter a 4-digit login code")
          .max(4, "Please enter a 4-digit login code")
          .matches(/^\d{4}$/, "Please enter a 4-digit numeric code"),
      otherwise: () => yup.string().notRequired(),
    }),

  isAwaitingCode: yup.boolean().required(),
});

type LoginCardProps = {
  afterLogin: () => void,
  headerText?: string | React.ReactNode,
  subheaderText?: string;
  isSignup?: boolean;
  formId?: string;
}

const LoginCard: React.FC<LoginCardProps> = (coreProps) => {
  const navigate = useNavigate();
  const { onLogin } = useContext(LoginContext);
  const [preflight] = useMutation<UserLoginPreflight, UserLoginPreflightVariables>(SIGN_IN_ATTEMPT);
  const [userLogin] = useMutation<UserLogin, UserLoginVariables>(USER_LOGIN);
  const [userPhoneLogin] = useMutation<UserPhoneLogin, UserPhoneLoginVariables>(USER_PHONE_LOGIN);
  const [sendSMSVerification] = useMutation<UserGetCode, UserGetCodeVariables>(USER_GET_CODE);

  const { inModal } = useContext(LoginModalContext);
  const location = useLocation();
  const hasLocationInitialized = useRef(false);

  const page = coreProps.isSignup ? "create" : "login";
  const [section, setSection] = useState<Section>({ page, props: { formId: coreProps.formId } });

  const [rememberedEmail, wasEmailRemembered, rememberedEmailOps] = useRememberedEmail();

  const query = new URLSearchParams(window.location.search);
  const username = query.get("u");

  const resetAwaitingCode = (setFieldValue: (field: string, value: any) => void) => {
    setFieldValue("isAwaitingCode", false);
    setFieldValue("code", "");
  };
  const sendCode = async (phoneNumber: string, handlers: FormikHelpers<LoginFormValues>) => {
    const number = sanitizePhoneNumber(phoneNumber);
    if (number.length !== 10) {
      onDisplayError(handlers, "Phone number is not valid.");
      return;
    }
    try {
      await sendSMSVerification({ variables: { username: phoneNumber } });
    } catch (err) {
      onDisplayError(handlers, err);
    }
  };

  const sharedProps = {
    onNavigate: (data: Section) => {
      if (!inModal && ["login", "create"].includes(data.page)) {
        const toPage = data.page === "login" ? "login" : "signup";
        navigate(`/${toPage}${data.props.formId ? `?fid=${data.props.formId}` : ""}`);
        return;
      }
      setSection(data);
    },
    children: null as React.ReactNode,
  };

  const toSignUp = () =>
    sharedProps.onNavigate({
      page: "create",
      props: { formId: coreProps.formId },
    });

  const login = (userId: string) => {
    initializeUserLogin({
      userId,
      roles: [""],
      anonymousUserId: getVisitorId(),
    });

    onLogin();
    coreProps.afterLogin();
  };

  const loginWithElixir = async (processedValues:LoginFormValues) => {
    const {
      data: loginData,
    } = await userLogin({ variables: {
      username: processedValues.identifier,
      password: processedValues.password,
    } });
    const token = loginData?.userLogin.token ?? null;
    if (token) {
      loginWithHavenToken(token);
      login(loginData?.userLogin.userId ?? "");
    } else {
      throw new Error("Incorrect password");
    }
  };

  const loginPhoneWithElixir = async (processedValues:LoginFormValues) => {
    const {
      data: loginData,
    } = await userPhoneLogin({ variables: {
      username: processedValues.identifier,
      code: processedValues.code,
    } });
    const token = loginData?.userPhoneLogin.token ?? null;
    if (token) {
      loginWithHavenToken(token);
      login(loginData?.userPhoneLogin.userId ?? "");
    } else {
      throw new Error("Invalid verification code");
    }
  };

  const onSubmitWithPreflight: FormikConfig<LoginFormValues>["onSubmit"] = useCallback(async (values, handlers) => {
    const processedValues = processValues(values);

    if (processedValues.isAwaitingCode) {
      try {
        await loginPhoneWithElixir(processedValues);
      } catch (err) {
        onDisplayError(handlers, err);
      }
      return;
    }

    const {
      data: result,
      errors,
    } = await preflight({ variables: { username: processedValues.identifier } });

    if (!result?.userLoginPreflight || errors) {
      onDisplayError(handlers, "Result is unexpectedly empty.");
      return;
    }

    const { userLoginPreflight } = result;

    if (userLoginPreflight.login === null) {
      onDisplayError(handlers, "No user account available");
      return;
    }

    switch (userLoginPreflight.login.method) {
      case LoginMethod.PHONE: {
        try {
          const number = sanitizePhoneNumber(processedValues.identifier);

          if (number.length !== 10) {
            onDisplayError(handlers, "Phone number is not valid.");
            return;
          }
          await sendSMSVerification({ variables: { username: processedValues.identifier } });
          handlers.setFieldValue("isAwaitingCode", true);
          handlers.setStatus({});
          handlers.setTouched({});
          handlers.setErrors({});
        } catch (err) {
          onDisplayError(handlers, err);
        }

        break;
      }

      case LoginMethod.EMAIL: {
        rememberedEmailOps.update(processedValues.remember, processedValues.identifier);

        if (userLoginPreflight.login.verified) {
          try {
            await loginWithElixir(processedValues);
          } catch (err) {
            onDisplayError(handlers, err);
          }
          return;
        }

        sharedProps.onNavigate({
          page: "wait-to-verify",
          props: { username: processedValues.identifier, formId: coreProps.formId },
        });

        break;
      }

      default:
        break;
    }
  }, []);

  // Keep internal section state in sync with route-provided isSignup prop
  useEffect(() => {
    // Do not override internal navigation when rendered inside a modal
    if (inModal) return;
    const desiredPage = coreProps.isSignup ? "create" : "login";
    // Only sync when on top-level auth pages; do not override intermediate flows
    if ((["login", "create"]).includes(section.page) && section.page !== desiredPage) {
      setSection({ page: desiredPage, props: {} });
    }
  }, [coreProps.isSignup, section.page, inModal]);

  // If user navigates via navbar to /login or /signup, reset the state accordingly
  useEffect(() => {
    if (!hasLocationInitialized.current) {
      hasLocationInitialized.current = true;
      return;
    }
    if (inModal) return;
    const path = location.pathname;
    if (path === "/login" && section.page !== "login") {
      setSection({ page: "login", props: {} });
    } else if (path === "/signup" && section.page !== "create") {
      setSection({ page: "create", props: {} });
    }
  }, [location.key, location.pathname, inModal]);

  if (section.page === "login") {
    return (
      <Formik
        enableReinitialize
        initialValues={{
          identifier: username || rememberedEmail,
          password: "",
          remember: wasEmailRemembered,
          code: "",
          showPassword: false,
          isAwaitingCode: false,
        }}
        validationSchema={schema}
        onSubmit={onSubmitWithPreflight}>
        {(handlers) => (
          <LoginFormContainer
            phoneNumberAwaitingCode={
              handlers.values.isAwaitingCode ? handlers.values.identifier : false
            }
            headerText={coreProps.headerText} subheaderText={coreProps.subheaderText} >
            {handlers.values.isAwaitingCode ? (
              <LoginFormMobile
                sendCode={() => sendCode(handlers.values.identifier, handlers)}
                resetAwaitingCode={() => resetAwaitingCode(handlers.setFieldValue)}
              />
            ) : <LoginForm toSignUp={toSignUp} />}
          </LoginFormContainer>)

        }
      </Formik>
    );
  }

  const Component = components[section.page] as Page<PageType>;
  const childProps = { ...sharedProps, ...section.props };
  if (section.page === "create") {
    childProps.children = <SignInLink onClick={() => sharedProps.onNavigate({ page: "login", props: {} })} />;
  }

  return <Component {...childProps} />;
};

export default LoginCard;
